import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Audit & compliance',
    metaDescription:
      'SOC 2, ISO 27001, GDPR, KVKK, PCI-DSS, HIPAA: pre-built control mappings, tamper-evident logs, and access reviews that finish.',
    heroEyebrow: 'Use cases · Audit & Compliance',
    heroTitle: ['Audit-ready,', 'not audit-panicked.'],
    heroBody:
      'Pre-built framework mappings, tamper-evident logs, scheduled access reviews, and SoD enforcement at request time. Monofor turns audit response from a four-week project into a one-click export.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'SOC 2 / ISO 27001 / GDPR / KVKK',
      'Tamper-evident evidence',
      'Auto-revoke on stale access',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'Compliance as a steady state, not a quarterly fire drill.',
    pillarsBody:
      'Most identity products generate audit data. Monofor packages it the way your assessors actually consume it, and runs the controls that produce it on a defined cadence.',
    pillars: [
      {
        title: 'Framework-aligned controls, pre-built',
        description:
          'SOC 2, ISO 27001, GDPR, KVKK, PCI-DSS, and HIPAA control mappings ship in the box. Monofor evidence flows straight into your assessor workflow.',
        bullets: [
          'Per-framework report templates',
          'Control-to-evidence mapping',
          'Configurable per-tenant retention',
          'Multi-jurisdiction data residency',
        ],
      },
      {
        title: 'Audit logs that hold up',
        description:
          'Immutable, tamper-evident logs. Cryptographic chain-of-custody on exports. Long retention without storage panic.',
        bullets: [
          'Hash-chained log entries',
          'Signed evidence bundle exports',
          'Configurable retention by sensitivity tier',
          'API access for SIEM and GRC integration',
        ],
      },
      {
        title: 'Access reviews that actually finish',
        description:
          'Quarterly reviews stop being a Slack-channel-driven fire drill. Monofor schedules, routes, escalates, and auto-revokes.',
        bullets: [
          'Manager- and app-owner-driven campaigns',
          'Risk tier prioritization',
          'Diff highlighting on recent changes',
          'Auto-revoke on overdue attestation',
        ],
      },
      {
        title: 'Separation of duties, enforced',
        description:
          'SoD rules block conflicting access at request time, not at audit time. Exceptions go through an explicit compensating-control flow.',
        bullets: [
          'Configurable SoD rule sets',
          'Real-time violation blocking',
          'Exception workflow with compensating controls',
          'Per-framework SoD reports',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Frameworks. Evidence. Export.',
    steps: [
      {
        title: 'Pick your frameworks',
        description:
          'Enable the framework templates you live under: SOC 2, ISO 27001, GDPR, KVKK, PCI-DSS, HIPAA. Mappings are pre-built.',
      },
      {
        title: 'Wire the evidence stream',
        description:
          'Audit logs, session recordings, and entitlement snapshots flow into the evidence layer continuously, not at audit kickoff.',
      },
      {
        title: 'Export on demand',
        description:
          'When the assessor asks, you export a signed evidence packet, instead of starting a four-week project.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Reporting & timeline',
        description: 'No-SQL report builder, scheduled delivery, access-change timeline.',
        href: '/sign/reporting',
      },
      {
        label: 'Privileged session recording',
        description: 'Tamper-evident video and keystroke evidence.',
        href: '/pam/session-recording',
      },
      {
        label: 'Zero Trust',
        description: 'Use case: making Zero Trust posture defensible.',
        href: '/solutions/zero-trust',
      },
    ],
  },
  tr: {
    metaTitle: 'Denetim ve uyum',
    metaDescription:
      'SOC 2, ISO 27001, GDPR, KVKK, PCI-DSS, HIPAA: hazır kontrol eşleştirmeleri, kurcalamaya karşı korumalı loglar ve gerçekten tamamlanan erişim gözden geçirmeleri.',
    heroEyebrow: 'Kullanım senaryoları · Denetim ve Uyum',
    heroTitle: ['Denetime hazır,', 'denetim paniği yok.'],
    heroBody:
      'Hazır çerçeve eşleştirmeleri, kurcalamaya karşı korumalı loglar, planlı erişim gözden geçirmeleri ve talep anında SoD uygulaması. Monofor, denetim yanıtını dört haftalık bir projeden tek tıkla dışa aktarmaya dönüştürür.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'SOC 2 / ISO 27001 / GDPR / KVKK',
      'Kurcalamaya karşı korumalı kanıt',
      'Bayat erişimde otomatik geri alma',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle:
      'Uyum, üç aylık bir yangın tatbikatı değil, kalıcı bir durum.',
    pillarsBody:
      'Çoğu kimlik ürünü denetim verisi üretir. Monofor bu veriyi denetçilerinizin gerçekten tükettiği biçimde paketler ve onu üreten kontrolleri tanımlı bir düzende çalıştırır.',
    pillars: [
      {
        title: 'Çerçeveyle hizalı kontroller, hazır gelir',
        description:
          'SOC 2, ISO 27001, GDPR, KVKK, PCI-DSS ve HIPAA kontrol eşleştirmeleri kutudan çıkar. Monofor kanıtları doğrudan denetçi iş akışınıza akar.',
        bullets: [
          'Çerçeve bazında rapor şablonları',
          'Kontrolden kanıta eşleştirme',
          'Kiracı bazında yapılandırılabilir saklama',
          'Çok yargı bölgeli veri yerleşimi',
        ],
      },
      {
        title: 'Ayakta kalan denetim logları',
        description:
          'Değiştirilemez, kurcalamaya karşı korumalı loglar. Dışa aktarımlarda kriptografik gözetim zinciri. Depolama paniği olmadan uzun saklama.',
        bullets: [
          'Hash zincirli log kayıtları',
          'İmzalı kanıt paketi dışa aktarımları',
          'Hassasiyet kademesine göre yapılandırılabilir saklama',
          'SIEM ve GRC entegrasyonu için API erişimi',
        ],
      },
      {
        title: 'Gerçekten tamamlanan erişim gözden geçirmeleri',
        description:
          'Üç aylık gözden geçirmeler, Slack kanalından yürüyen bir yangın tatbikatı olmaktan çıkar. Monofor planlar, yönlendirir, eskalasyon yapar ve otomatik geri alır.',
        bullets: [
          'Yönetici ve uygulama sahibi güdümlü kampanyalar',
          'Risk kademesine göre önceliklendirme',
          'Son değişikliklerde fark vurgulama',
          'Gecikmiş doğrulamada otomatik geri alma',
        ],
      },
      {
        title: 'Görevler ayrılığı, fiilen uygulanır',
        description:
          'SoD kuralları çakışan erişimi denetim anında değil, talep anında engeller. İstisnalar açık bir telafi edici kontrol akışından geçer.',
        bullets: [
          'Yapılandırılabilir SoD kural setleri',
          'Gerçek zamanlı ihlal engelleme',
          'Telafi edici kontroller içeren istisna iş akışı',
          'Çerçeve bazında SoD raporları',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Çerçeveler. Kanıt. Dışa aktarım.',
    steps: [
      {
        title: 'Çerçevelerinizi seçin',
        description:
          'Tabi olduğunuz çerçeve şablonlarını etkinleştirin: SOC 2, ISO 27001, GDPR, KVKK, PCI-DSS, HIPAA. Eşleştirmeler hazır gelir.',
      },
      {
        title: 'Kanıt akışını bağlayın',
        description:
          'Denetim logları, oturum kayıtları ve yetki anlık görüntüleri kanıt katmanına denetim başlangıcında değil, sürekli olarak akar.',
      },
      {
        title: 'Talep üzerine dışa aktarın',
        description:
          'Denetçi istediğinde, dört haftalık bir proje başlatmak yerine imzalı bir kanıt paketini dışa aktarırsınız.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Raporlama ve zaman çizelgesi',
        description:
          'SQL gerektirmeyen rapor oluşturucu, zamanlanmış teslimat, erişim değişikliği çizelgesi.',
        href: '/sign/reporting',
      },
      {
        label: 'Ayrıcalıklı oturum kaydı',
        description: 'Kurcalamaya karşı korumalı video ve tuş vuruşu kanıtı.',
        href: '/pam/session-recording',
      },
      {
        label: 'Sıfır Güven',
        description:
          'Kullanım senaryosu: Sıfır Güven duruşunu savunulabilir kılmak.',
        href: '/solutions/zero-trust',
      },
    ],
  },
}

type Props = { locale: string }

export default function AuditCompliancePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/audit-compliance"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
